import React, { useState, useEffect } from 'react';
import { getPlansService, rechargeService, getGatewaysService, captureService } from '../../services/tokenService';
import { useAuthContext } from '../../context/AuthProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import './TokenPlans.css';
import Loader from '../../components/shared/Loader';

const TokenPlans = () => {
    const { user, updateBalance, updateAccess, access } = useAuthContext();
    const token = user?.token;
    const location = useLocation();
    const navigate = useNavigate();
    
    const [plans, setPlans] = useState([]);
    const [gateways, setGateways] = useState([]);
    const [selectedGateway, setSelectedGateway] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [recharging, setRecharging] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const status = queryParams.get('status');
        const referenceId = queryParams.get('reference_id')||queryParams.get('token');

        if (status === 'success' && referenceId) {
            handleCapture(referenceId);
        } else if (status === 'cancel') {
            setError('La transacción fue cancelada.');
            navigate(location.pathname, { replace: true });
        }

        const fetchData = async () => {
            try {
                const [plansData, gatewaysData] = await Promise.all([
                    getPlansService(token),
                    getGatewaysService(token)
                ]);
                setPlans(plansData.filter(plan => plan.is_active));
                setGateways(gatewaysData);
                if (gatewaysData.length > 0) {
                    setSelectedGateway((gatewaysData[0].id || gatewaysData[0].name).toLowerCase());
                }
                setLoading(false);
            } catch (err) {
                setError('Error al cargar la información. Inténtalo de nuevo más tarde.');
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, location.search]);

    const handleCapture = async (referenceId) => {
        setLoading(true);
        try {
            const response = await captureService({ reference_id: referenceId }, token);
            if (response.balance !== undefined) {
                updateBalance(response.balance);
                updateAccess(response.access);
                setSuccess(response.message || '¡Recarga exitosa!');
                const backTo = location.state?.from || '/live';
                setTimeout(() => {
                    navigate(backTo);
                }, 3000);
            } else {
                setError('No se pudo confirmar la transacción.');
            }
        } catch (err) {
            setError(err.message || 'Error al capturar la transacción.');
        } finally {
            setLoading(false);
            navigate(location.pathname, { replace: true });
        }
    };

    const handleRecharge = async (planId) => {
        if (!selectedGateway) {
            setError('Por favor, selecciona una pasarela de pago.');
            return;
        }

        setRecharging(true);
        setError(null);
        setSuccess(null);
        try {
            const body = {
                plan_id: planId,
                gateway: selectedGateway,
                return_url: window.location.origin + '/recharge?status=success',
                cancel_url: window.location.origin + '/recharge?status=cancel'
            };
            const response = await rechargeService(body, token);
            if (response.redirect_url) {
                window.location.href = response.redirect_url;
            } else {
                setError('No se pudo iniciar la recarga.');
                setRecharging(false);
            }
        } catch (err) {
            setError('Error al procesar la recarga.');
            setRecharging(false);
        }
    };

    // El motivo lo decide el backend (`denial_reason`); aquí solo se traduce.
    const reason = location.state?.reason || (access?.can_watch_live ? null : access?.denial_reason);
    const blockReason = {
        'no-subscription': `Aún no tienes una recarga activa. Recarga para abrir tu acceso de ${access?.window_days ?? 30} días al contenido en vivo.`,
        'subscription-expired': 'Tu periodo de acceso venció. Tus tokens siguen en tu cuenta: recarga para volver a usarlos y entrar a las salas.',
        'no-tokens': 'Te quedaste sin tokens. Recarga para seguir viendo el contenido en vivo.',
        'no-balance': 'Necesitas tokens para entrar al show en vivo.',
        'unavailable': 'No pudimos verificar tu acceso. Intenta recargar la página.',
    }[reason] || null;

    return (
        <div className="token-plans-container">
            <Loader loading={loading} message="Cargando planes" />
            <Loader loading={recharging} message="Procesando pago" />
            <div className="token-plans-header">
                <h2>Recarga tus Tokens</h2>
                <p>Selecciona una pasarela de pago y el plan que prefieras.</p>
            </div>

            {/* Llegar aquí desde una sala antes parecía un error de navegación;
                ahora se explica el motivo exacto y a dónde se vuelve. */}
            {blockReason && (
                <div className="alert alert-warning d-flex align-items-start gap-2">
                    <i className="fa-solid fa-circle-info mt-1"></i>
                    <span>{blockReason}</span>
                </div>
            )}

            {/* Estado de la ventana de acceso, siempre visible */}
            {access && !access.is_exempt && (
                <div className="subscription-status mb-4">
                    <div className="subscription-status-item">
                        <span className="label">Tokens disponibles</span>
                        <strong>{access.tokens}</strong>
                    </div>
                    <div className="subscription-status-item">
                        <span className="label">Acceso al vivo</span>
                        <strong className={access.subscription_active ? 'text-success' : 'text-danger'}>
                            {access.subscription_active
                                ? `${access.days_remaining} ${access.days_remaining === 1 ? 'día' : 'días'} restantes`
                                : 'Vencido'}
                        </strong>
                    </div>
                    {access.subscription_expires_at && (
                        <div className="subscription-status-item">
                            <span className="label">Vence el</span>
                            <strong>{new Date(access.subscription_expires_at).toLocaleDateString()}</strong>
                        </div>
                    )}
                </div>
            )}

            {error && <div className="alert alert-danger alert-dismissible fade show">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>}

            {success && <div className="alert alert-success alert-dismissible fade show">
                <i className="fa-solid fa-circle-check me-2"></i>
                {success}
                <p className="small mb-0">Serás redirigido en unos segundos...</p>
            </div>}

            <div className="gateway-selector mb-5">
                <h4 className="mb-4 text-white-50 small text-uppercase fw-bold" style={{letterSpacing: '2px'}}>1. Selecciona Pasarela</h4>
                <div className="gateway-options">
                    {gateways.map(gw => (
                        <div 
                            key={gw.id} 
                            className={`gateway-card ${selectedGateway === gw.id.toLowerCase() ? 'active' : ''}`}
                            onClick={() => setSelectedGateway(gw.id.toLowerCase())}
                        >
                            <div className="gateway-icon">
                                {gw.icon ? <i className={gw.icon}></i> : <i className="fa-solid fa-credit-card"></i>}
                            </div>
                            <span className="gateway-name">{gw.name}</span>
                            {selectedGateway === gw.id.toLowerCase() && (
                                <div className="selected-badge">
                                    <i className="fa-solid fa-check"></i>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="plan-selector">
                <h4 className="mb-4 text-white-50 small text-uppercase fw-bold" style={{letterSpacing: '2px'}}>2. Elige tu Plan</h4>
                <div className="plans-grid">
                    {plans.map((plan) => (
                        <div key={plan.id} className="plan-card">
                            {plan.tokens >= 1000 && <div className="plan-badge">Popular</div>}
                            <div className="plan-header">
                                <h3>{plan.name}</h3>
                                <div className="plan-tokens">
                                    <span className="token-amount">{plan.tokens}</span>
                                    <span className="token-label">Tokens</span>
                                </div>
                            </div>
                            <div className="plan-price">
                                <span className="currency">{plan.currency}</span>
                                <span className="price-amount">{plan.price}</span>
                            </div>
                            <ul className="plan-features">
                                <li><i className="fa-solid fa-check"></i> Acceso inmediato</li>
                                <li><i className="fa-solid fa-check"></i> Señales en tiempo real</li>
                                <li><i className="fa-solid fa-check"></i> Soporte 24/7</li>
                            </ul>
                            <button 
                                className="btn-recharge" 
                                disabled={recharging || loading || !selectedGateway}
                                onClick={() => handleRecharge(plan.id)}
                            >
                                {recharging ? 'Procesando...' : 'Comprar Ahora'}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TokenPlans;
